import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchAuditLog,
  fetchOperatorTenants,
  fetchJSON,
  fetchSuperAdminDashboardStats,
  fetchSuperAdminRecentInvitations,
  fetchSuperAdminSystemStatus,
  fetchSuperAdminTenants,
} from "../../api.js";
import ErrorBoundary from "../../components/ErrorBoundary.jsx";
import { usePortalAuth } from "../components/RequireClient.jsx";
import { getTenantWorkspaceUrl } from "../tenantWorkspaceUrl.js";
import "./SuperAdminDashboard.css";

const initialPanel = (data = null) => ({ data, loading: false, error: "", updatedAt: null });

function display(value, fallback = "—") {
  return value === null || value === undefined || value === "" ? fallback : String(value);
}

function dateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
}

function age(value) {
  if (!value) return "—";
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return "—";
  const minutes = Math.max(0, Math.floor((Date.now() - timestamp) / 60000));
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return hours < 24 ? `${hours}h` : `${Math.floor(hours / 24)}d`;
}

function label(value) {
  return display(value, "Unknown")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function StatusBadge({ value = "unknown" }) {
  const normalized = String(value || "unknown").toLowerCase();
  const tone = ["healthy", "active", "accepted", "available"].includes(normalized)
    ? "success"
    : ["critical", "suspended", "urgent", "failed"].includes(normalized)
      ? "danger"
      : ["attention", "trial", "pending"].includes(normalized)
        ? "warning"
        : "neutral";
  return (
    <span className={`sa-status sa-status--${tone}`}>
      <span aria-hidden="true" className="sa-status__dot" />
      {label(normalized)}
    </span>
  );
}

function ConsoleSection({ title, description, action, panel, children, className = "" }) {
  return (
    <section className={`sa-section ${className}`} aria-busy={panel?.loading || undefined}>
      <header className="sa-section__header">
        <div>
          <h2>{title}</h2>
          {description && <p>{description}</p>}
        </div>
        {action}
      </header>
      {panel?.error && <PanelError message={panel.error} stale={Boolean(panel.data)} />}
      {panel?.loading && !panel.data ? <PanelLoading /> : children}
    </section>
  );
}

function PanelLoading() {
  return (
    <div className="sa-panel-state">
      <span className="sa-spinner" aria-hidden="true" />
      Loading…
    </div>
  );
}
function PanelError({ message, stale }) {
  return (
    <div className="sa-panel-error" role="alert">
      <strong>{stale ? "Refresh failed; showing previous data." : "Panel unavailable."}</strong> {message}
    </div>
  );
}
function EmptyState({ children }) {
  return <div className="sa-panel-state">{children}</div>;
}

function Metric({ label: metricLabel, value, detail, tone }) {
  return (
    <div className={`sa-metric${tone ? ` sa-metric--${tone}` : ""}`}>
      <dt>{metricLabel}</dt>
      <dd>{display(value, "Unavailable")}</dd>
      <small>{detail}</small>
    </div>
  );
}

function NotInstrumented() {
  return (
    <div className="sa-not-instrumented">
      <StatusBadge value="not_instrumented" />
      <div>
        <strong>Operations telemetry not instrumented</strong>
        <p>Job runs, infrastructure capacity, dependency availability, and reconciliation history have no approved telemetry source.</p>
      </div>
    </div>
  );
}

function TenantAccessPicker({ state, search, onSearch, onClose }) {
  const dialogRef = useRef(null);
  const tenants = (state.data?.tenants || []).filter((tenant) => {
    const query = search.trim().toLowerCase();
    return !query || tenant.name?.toLowerCase().includes(query) || tenant.slug?.toLowerCase().includes(query);
  });
  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = Array.from(
      dialogRef.current?.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ) || [],
    ).filter((element) => !element.hasAttribute("hidden") && element.getAttribute("aria-hidden") !== "true");
    if (!focusable.length) {
      event.preventDefault();
      dialogRef.current?.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  return (
    <div
      className="sa-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}>
      <section ref={dialogRef} className="sa-modal" role="dialog" aria-modal="true" aria-labelledby="tenant-access-heading" tabIndex={-1} onKeyDown={handleKeyDown}>
        <header>
          <div>
            <h2 id="tenant-access-heading">Access tenant workspace</h2>
            <p>Select a tenant to open its canonical workspace as your global super-admin identity.</p>
          </div>
          <button className="sa-button sa-button--text" type="button" onClick={onClose}>
            Close
          </button>
        </header>
        <div className="sa-modal__body">
          <label className="sa-search-label">
            Search tenants
            <input autoFocus value={search} onChange={(event) => onSearch(event.target.value)} placeholder="Name or slug" />
          </label>
          {state.error && <PanelError message={state.error} />}
          {state.loading ? (
            <PanelLoading />
          ) : tenants.length ? (
            <div className="sa-tenant-picker-list">
              {tenants.map((tenant) => {
                const workspaceUrl = getTenantWorkspaceUrl(tenant.slug);
                return (
                  <div key={tenant.id}>
                    <span>
                      <strong>{tenant.name}</strong>
                      <small>{tenant.slug || "No tenant slug"}</small>
                    </span>
                    {workspaceUrl ? (
                      <a className="sa-button" href={workspaceUrl}>
                        Access workspace
                      </a>
                    ) : (
                      <span className="sa-unavailable">Workspace URL unavailable</span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            !state.error && <EmptyState>No tenants match this search.</EmptyState>
          )}
        </div>
      </section>
    </div>
  );
}

function ProvisionForm({ onClose, onProvisioned }) {
  const [form, setForm] = useState({ tenantName: "", adminEmail: "", adminFirstName: "", adminLastName: "", seats: "2" });
  const [state, setState] = useState({ busy: false, error: "", success: "" });
  const submit = async (event) => {
    event.preventDefault();
    const payload = {
      ...form,
      tenantName: form.tenantName.trim(),
      adminEmail: form.adminEmail.trim(),
      adminFirstName: form.adminFirstName.trim() || null,
      adminLastName: form.adminLastName.trim() || null,
      seats: Number(form.seats),
    };
    if (!payload.tenantName || !payload.adminEmail || !Number.isInteger(payload.seats) || payload.seats < 1)
      return setState({ busy: false, error: "Enter a tenant name, valid admin email, and at least one seat.", success: "" });
    setState({ busy: true, error: "", success: "" });
    try {
      const body = await fetchJSON("/api/superadmin/tenants/provision", {
        method: "POST",
        json: payload,
        portal: true,
      });
      setState({ busy: false, error: "", success: `Tenant ${body?.tenant?.name || payload.tenantName} was provisioned.` });
      setForm({ tenantName: "", adminEmail: "", adminFirstName: "", adminLastName: "", seats: "2" });
      onProvisioned();
    } catch (error) {
      setState({ busy: false, error: error?.message || "Unable to provision tenant.", success: "" });
    }
  };
  return (
    <section className="sa-provision" aria-labelledby="provision-heading">
      <header>
        <div>
          <h2 id="provision-heading">Quick Provision Client</h2>
          <p>This explicit action creates a tenant and sends the owner invitation.</p>
        </div>
        <button type="button" className="sa-button sa-button--text" onClick={onClose}>
          Close
        </button>
      </header>
      <form onSubmit={submit}>
        <label>
          Tenant name *<input required name="tenantName" value={form.tenantName} onChange={(e) => setForm({ ...form, tenantName: e.target.value })} disabled={state.busy} />
        </label>
        <label>
          Admin email *
          <input required type="email" name="adminEmail" value={form.adminEmail} onChange={(e) => setForm({ ...form, adminEmail: e.target.value })} disabled={state.busy} />
        </label>
        <label>
          First name
          <input name="adminFirstName" value={form.adminFirstName} onChange={(e) => setForm({ ...form, adminFirstName: e.target.value })} disabled={state.busy} />
        </label>
        <label>
          Last name
          <input name="adminLastName" value={form.adminLastName} onChange={(e) => setForm({ ...form, adminLastName: e.target.value })} disabled={state.busy} />
        </label>
        <label>
          Seats
          <input required type="number" min="1" name="seats" value={form.seats} onChange={(e) => setForm({ ...form, seats: e.target.value })} disabled={state.busy} />
        </label>
        <button className="sa-button sa-button--primary" disabled={state.busy}>
          {state.busy ? "Provisioning…" : "Provision client"}
        </button>
      </form>
      {state.error && (
        <p className="sa-form-message sa-form-message--error" role="alert">
          {state.error}
        </p>
      )}
      {state.success && (
        <p className="sa-form-message sa-form-message--success" role="status">
          {state.success}
        </p>
      )}
    </section>
  );
}

export default function SuperAdminDashboard() {
  const { memberRole } = usePortalAuth();
  const authorized = memberRole === "super_admin";
  const [panels, setPanels] = useState({ stats: initialPanel(), tenants: initialPanel(), audit: initialPanel(), invitations: initialPanel(), system: initialPanel() });
  const [refreshToken, setRefreshToken] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [showProvision, setShowProvision] = useState(false);
  const [showTenantAccess, setShowTenantAccess] = useState(false);
  const [tenantSearch, setTenantSearch] = useState("");
  const [tenantAccess, setTenantAccess] = useState(initialPanel());
  const provisionRef = useRef(null);
  const tenantAccessTriggerRef = useRef(null);
  const mountedRef = useRef(false);
  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    if (!authorized) return;
    const requestId = ++requestIdRef.current;
    const requests = {
      stats: fetchSuperAdminDashboardStats(),
      tenants: fetchOperatorTenants({ page: 1, pageSize: 10, sort: "lastActivity", direction: "desc" }),
      audit: fetchAuditLog({ page: 1, pageSize: 10 }),
      invitations: fetchSuperAdminRecentInvitations({ limit: 10 }),
      system: fetchSuperAdminSystemStatus(),
    };
    setRefreshing(true);
    setPanels((current) => Object.fromEntries(Object.entries(current).map(([key, panel]) => [key, { ...panel, loading: true, error: "" }])));
    const keys = Object.keys(requests);
    const settled = await Promise.allSettled(Object.values(requests));
    if (!mountedRef.current || requestId !== requestIdRef.current) return;
    const completedAt = new Date().toISOString();
    setPanels((current) => {
      const next = { ...current };
      settled.forEach((result, index) => {
        const key = keys[index];
        next[key] =
          result.status === "fulfilled"
            ? { data: result.value, loading: false, error: "", updatedAt: completedAt }
            : { ...current[key], loading: false, error: result.reason?.message || "Request failed." };
      });
      return next;
    });
    setRefreshing(false);
  }, [authorized]);

  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => {
      mountedRef.current = false;
      requestIdRef.current += 1;
    };
  }, [load, refreshToken]);

  if (!authorized)
    return (
      <main className="sa-dashboard">
        <div className="sa-access-denied">
          <h1>Access denied</h1>
          <p>Super-admin authorization is required.</p>
        </div>
      </main>
    );

  const stats = panels.stats.data || {};
  const tenantRows = panels.tenants.data?.items || [];
  const auditRows = panels.audit.data?.items || panels.audit.data?.entries || [];
  const invites = panels.invitations.data?.invitations || [];
  const deployment = panels.system.data?.deployment || {};
  const lastUpdated = Object.values(panels)
    .map((panel) => panel.updatedAt)
    .filter(Boolean)
    .sort()
    .at(-1);

  const openProvision = () => {
    setShowProvision(true);
    requestAnimationFrame(() => {
      if (typeof provisionRef.current?.scrollIntoView === "function") provisionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };
  const refresh = () => setRefreshToken((value) => value + 1);
  const openTenantAccess = async () => {
    setShowTenantAccess(true);
    setTenantSearch("");
    setTenantAccess((current) => ({ ...current, loading: true, error: "" }));
    try {
      const data = await fetchSuperAdminTenants({ limit: 500 });
      if (mountedRef.current) setTenantAccess({ data, loading: false, error: "", updatedAt: new Date().toISOString() });
    } catch (error) {
      if (mountedRef.current) setTenantAccess((current) => ({ ...current, loading: false, error: error?.message || "Unable to load tenants." }));
    }
  };
  const closeTenantAccess = () => {
    setShowTenantAccess(false);
    requestAnimationFrame(() => tenantAccessTriggerRef.current?.focus());
  };

  return (
    <ErrorBoundary componentName="SuperAdminDashboard">
      <main className="sa-dashboard">
        <div className="sa-breadcrumb">
          <Link to="/portal/overview">Portal</Link>
          <span>/</span>
          <span>Super Admin</span>
          <span>/</span>
          <strong>Overview</strong>
        </div>
        <header className="sa-page-header">
          <div>
            <h1>Super Admin Dashboard</h1>
            <p>Tenant, security, support, deployment, and operational visibility.</p>
            <div className="sa-header-meta">
              <span>
                Environment: <strong>{display(deployment.environment, "Unavailable")}</strong>
              </span>
              <span>
                Last refreshed: <strong>{dateTime(lastUpdated)}</strong>
              </span>
            </div>
          </div>
          <div className="sa-header-actions">
            <button className="sa-button sa-button--primary" type="button" onClick={openProvision}>
              Provision client
            </button>
            <button ref={tenantAccessTriggerRef} className="sa-button" type="button" onClick={openTenantAccess}>
              Access tenant
            </button>
            <Link className="sa-button" to="/portal/admin/support">
              Support requests
            </Link>
            <Link className="sa-button" to="/portal/ops/audit">
              Audit log
            </Link>
            <button className="sa-button" type="button" onClick={refresh} disabled={refreshing}>
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </header>

        <ConsoleSection title="Attention" description="Authoritative queue totals and tenant states requiring review." panel={panels.stats}>
          {panels.stats.data && (
            <div className="sa-attention">
              <Link to="/portal/admin/tenants?status=suspended">
                <strong>{display(stats.tenants?.suspended, 0)}</strong>
                <span>Suspended tenants</span>
              </Link>
              <Link to="/portal/admin/support">
                <strong>{display(stats.support?.urgent, 0)}</strong>
                <span>Urgent support</span>
              </Link>
              <Link to="/portal/admin/support">
                <strong>{display(stats.accessRequests?.pending, 0)}</strong>
                <span>Pending access</span>
              </Link>
              <Link to="/portal/admin/invite">
                <strong>{display(stats.invitations?.pending, 0)}</strong>
                <span>Pending invitations</span>
              </Link>
            </div>
          )}
        </ConsoleSection>

        <ConsoleSection title="Resource summary" description={`Aggregate snapshot${stats.generatedAt ? ` generated ${dateTime(stats.generatedAt)}` : ""}.`} panel={panels.stats}>
          {panels.stats.data && (
            <dl className="sa-metrics">
              <Metric label="Active tenants" value={stats.tenants?.active} detail={`${display(stats.tenants?.total, "—")} total`} />
              <Metric label="Active memberships" value={stats.memberships?.active} detail={`${display(stats.memberships?.suspended, "—")} suspended`} />
              <Metric label="Invitations" value={stats.invitations?.total} detail={`${display(stats.invitations?.pending, "—")} pending`} />
              <Metric label="Open support" value={stats.support?.open} detail={`Oldest: ${dateTime(stats.support?.oldestUnresolvedAt)}`} />
              <Metric label="Pending access" value={stats.accessRequests?.pending} detail={`Oldest: ${dateTime(stats.accessRequests?.oldestPendingAt)}`} />
            </dl>
          )}
        </ConsoleSection>

        <ConsoleSection
          title="Tenant operational health"
          description="Configuration and workflow risk—not service uptime."
          panel={panels.tenants}
          action={<Link to="/portal/admin/tenants">View all tenants</Link>}>
          {panels.tenants.data &&
            (tenantRows.length ? (
              <div className="sa-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Tenant</th>
                      <th>Status</th>
                      <th>Operational risk</th>
                      <th>Members</th>
                      <th>Seats</th>
                      <th>Clerk org</th>
                      <th>Support</th>
                      <th>Last activity</th>
                      <th>
                        <span className="sr-only">Open</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenantRows.map((tenant) => (
                      <tr key={tenant.id}>
                        <td>
                          <strong>{tenant.name}</strong>
                          <small>{tenant.slug}</small>
                        </td>
                        <td>
                          <StatusBadge value={tenant.status} />
                        </td>
                        <td>
                          <StatusBadge value={tenant.health?.status} />
                          <small>{tenant.health?.reasons?.[0]?.label || "No configured risk rule matched"}</small>
                        </td>
                        <td>{display(tenant.members?.active)}</td>
                        <td>
                          {display(tenant.seats?.used)} / {tenant.seats?.total == null ? "Unlimited" : tenant.seats.total}
                        </td>
                        <td>{tenant.authentication?.linked ? "Linked" : "Not linked"}</td>
                        <td>{display(tenant.support?.open, 0)} open</td>
                        <td>{dateTime(tenant.lastActivityAt)}</td>
                        <td>
                          <Link aria-label={`View ${tenant.name}`} to={`/portal/admin/tenants/${encodeURIComponent(tenant.id)}`}>
                            View
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState>No tenants found.</EmptyState>
            ))}
        </ConsoleSection>

        <div className="sa-grid sa-grid--two">
          <ConsoleSection
            title="Recent administrative activity"
            description="Minimized audit projection; sensitive request metadata is excluded."
            panel={panels.audit}
            action={<Link to="/portal/ops/audit">Investigate</Link>}>
            {panels.audit.data &&
              (auditRows.length ? (
                <div className="sa-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>Tenant</th>
                        <th>Actor</th>
                        <th>Action</th>
                        <th>Resource</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditRows.map((event) => (
                        <tr key={event.id}>
                          <td title={dateTime(event.createdAt)}>{age(event.createdAt)}</td>
                          <td>{event.tenantName || "System"}</td>
                          <td>{[event.actorFirstName, event.actorLastName].filter(Boolean).join(" ") || event.actorEmail || "System"}</td>
                          <td>
                            <code>{event.action}</code>
                          </td>
                          <td>{event.targetType ? `${event.targetType}${event.targetId ? ` · ${event.targetId}` : ""}` : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState>No recent audit events.</EmptyState>
              ))}
          </ConsoleSection>
          <ConsoleSection
            title="Recent Clerk invitations"
            description="Local invitation records; this is not a Clerk availability check."
            panel={panels.invitations}
            action={<Link to="/portal/admin/invite">Manage invitations</Link>}>
            {panels.invitations.data &&
              (invites.length ? (
                <div className="sa-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Tenant</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Invited</th>
                        <th>Expires</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invites.map((invite) => (
                        <tr key={invite.id}>
                          <td>{invite.email}</td>
                          <td>{invite.tenantName || "—"}</td>
                          <td>{label(invite.role)}</td>
                          <td>
                            <StatusBadge value={invite.status} />
                          </td>
                          <td>{dateTime(invite.invitedAt)}</td>
                          <td>{dateTime(invite.acceptedAt || invite.expiresAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState>No recent invitations.</EmptyState>
              ))}
          </ConsoleSection>
        </div>

        <div className="sa-grid sa-grid--two">
          <ConsoleSection title="Service and deployment state" description="Build metadata is allowlisted. No dependency probes run on dashboard load." panel={panels.system}>
            {panels.system.data && (
              <>
                <dl className="sa-definition-grid">
                  <div>
                    <dt>Backend version</dt>
                    <dd>{display(deployment.backendVersion, "Unavailable")}</dd>
                  </div>
                  <div>
                    <dt>Backend commit</dt>
                    <dd>{display(deployment.backendCommit, "Unavailable")}</dd>
                  </div>
                  <div>
                    <dt>Backend deployed</dt>
                    <dd>{dateTime(deployment.backendDeployedAt)}</dd>
                  </div>
                  <div>
                    <dt>Frontend version</dt>
                    <dd>{display(deployment.frontendVersion, "Unavailable")}</dd>
                  </div>
                  <div>
                    <dt>Frontend commit</dt>
                    <dd>{display(deployment.frontendCommit, "Unavailable")}</dd>
                  </div>
                  <div>
                    <dt>Frontend built</dt>
                    <dd>{dateTime(deployment.frontendBuiltAt)}</dd>
                  </div>
                </dl>
                <NotInstrumented />
              </>
            )}
          </ConsoleSection>
          <ConsoleSection title="Estimated database statistics" description="MySQL information-schema estimates; not a database health check." panel={panels.stats}>
            {panels.stats.data &&
              (stats.database?.length ? (
                <div className="sa-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Table</th>
                        <th>Estimated rows</th>
                        <th>Data size</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.database.map((row) => (
                        <tr key={row.table || row.table_name}>
                          <td>
                            <code>{row.table || row.table_name}</code>
                          </td>
                          <td>{display(row.rows ?? row.table_rows)}</td>
                          <td>{row.sizeMB ?? row.size_mb ?? "—"} MB</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState>Database statistics unavailable.</EmptyState>
              ))}
          </ConsoleSection>
        </div>

        {showProvision && (
          <div ref={provisionRef}>
            <ProvisionForm onClose={() => setShowProvision(false)} onProvisioned={refresh} />
          </div>
        )}
        {showTenantAccess && <TenantAccessPicker state={tenantAccess} search={tenantSearch} onSearch={setTenantSearch} onClose={closeTenantAccess} />}
      </main>
    </ErrorBoundary>
  );
}
